import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Box, CircularProgress, Alert } from '@mui/material';
import useChartData from '../hooks/useChartData';
import SharedTooltip from './SharedTooltip';
import { AXIS_STYLE, GRID_STYLE } from '../constants/ChartConfig';

const TeamSimulationChart = ({ selectedTeams, dataType, startPlace, endPlace }) => {
  // dataType może być: 'avgPoints', 'avgPosition' lub 'probability'
  
  const { data: rawResults, loading, error } = useChartData(
    selectedTeams,
    (id) => `/api/team/${id}/position-chances`, // Ten sam endpoint
    { startPlace, endPlace },
    true
  );

  const config = {
    avgPoints: { label: 'Śr. punktów', reversed: false, formatter: (v) => v.toFixed(1) },
    avgPosition: { label: 'Śr. pozycja', reversed: true, formatter: (v) => v.toFixed(1) },
    probability: { label: 'Szanse', reversed: false, formatter: (v) => `${(v * 100).toFixed(0)}%` }
  }[dataType];

  const chartData = useMemo(() => {
    const dataMap = {};
    rawResults.forEach(res => {
      (res.rawData[0]?.series || []).forEach(p => {
        const ts = new Date(p.createdAtDate).getTime();
        if (!dataMap[ts]) dataMap[ts] = { timestamp: ts, newMatchesByTeam: {} };
        
        // Dynamicznie wybieramy klucz (avgPoints, avgPosition, probability)
        dataMap[ts][res.team.teamName] = p[dataType]; 
        
        if (p.newMatches?.length) dataMap[ts].newMatchesByTeam[res.team.teamName] = p.newMatches;
      });
    });
    return Object.values(dataMap).sort((a, b) => a.timestamp - b.timestamp);
  }, [rawResults, dataType]);

  // Logika osi X (identyczna jak wcześniej)
  const xDomain = useMemo(() => chartData.length ? [chartData[0].timestamp, chartData[chartData.length - 1].timestamp] : [0, 0], [chartData]);
  const monthTicks = useMemo(() => {
    if (!chartData.length) return [];
    const ticks = [];
    let curr = new Date(xDomain[0]);
    curr.setDate(1);
    while (curr.getTime() <= xDomain[1]) {
      if (curr.getTime() >= xDomain[0]) ticks.push(curr.getTime());
      curr.setMonth(curr.getMonth() + 1);
    }
    return ticks;
  }, [xDomain, chartData]);

  if (loading && chartData.length === 0) return <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ width: '100%', height: 450 }}>
      <ResponsiveContainer>
        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 40 }}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis 
            {...AXIS_STYLE} 
            type="number" 
            dataKey="timestamp" 
            domain={xDomain} 
            ticks={monthTicks}
            tickFormatter={v => `${String(new Date(v).getMonth() + 1).padStart(2,'0')}/${String(new Date(v).getFullYear()).slice(-2)}`} 
          />
          <YAxis 
            {...AXIS_STYLE} 
            reversed={config.reversed} // Dla pozycji: 1 na górze, 18 na dole
            tickFormatter={config.formatter} 
            domain={['auto', 'auto']} 
            angle={0} textAnchor="end" height={0} 
          />
          <Tooltip content={<SharedTooltip isTimestamp />} shared />
          {selectedTeams.map(t => (
            <Line key={t.teamName} name={t.teamName} dataKey={t.teamName} stroke={t.color} strokeWidth={3} type="monotone" dot={{ r: 2 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TeamSimulationChart;